import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { LogOut, Menu, Search, User as UserIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { PeriodBadge } from './PeriodBadge';
import { LanguageToggle } from './LanguageToggle';
import { ThemeToggle } from './ThemeToggle';
import { HelpMenu } from '@/features/onboarding/HelpMenu';
import { useAuth } from '@/features/auth/AuthContext';
import { useUiStore } from '@/stores/ui.store';
import { useSections } from '@/features/sections/sections.queries';

/**
 * Topbar — docs/04 §1. Sticky, h-14, bottom border.
 *
 * The active section's colour tints the bar so the user always knows which books they
 * are writing into (docs/03 §5) — the one piece of chrome that is allowed to be loud.
 */
export const Topbar = ({ onOpenSearch }) => {
  const { t } = useTranslation(['common', 'nav']);
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  const setSidebarOpen = useUiStore((state) => state.setSidebarOpen);
  const activeSectionId = useUiStore((state) => state.activeSectionId);
  const { data: sections = [] } = useSections();

  const activeSection = sections.find((section) => section.id === activeSectionId) || null;
  const accent = activeSection?.color || 'var(--section-hq)';

  return (
    <header
      className="sticky top-0 z-30 flex h-topbar shrink-0 items-center gap-2 border-b bg-card/95 px-4 backdrop-blur-md supports-[backdrop-filter]:bg-card/75"
      // Rebinding the variable here means every `bg-section`/accent consumer below the
      // topbar follows the active section without prop-drilling a colour.
      style={{ '--section-active': accent }}
    >
      <span
        aria-hidden="true"
        className="absolute inset-x-0 top-0 h-0.5"
        style={{ backgroundColor: `hsl(${accent})` }}
      />

      <Button
        variant="ghost"
        size="icon-sm"
        className="lg:hidden"
        onClick={() => setSidebarOpen(true)}
        aria-label={t('common:sidebar.expand')}
      >
        <Menu className="size-4" />
      </Button>

      <div className="flex items-center gap-2.5 font-semibold">
        <span className="flex size-7 items-center justify-center rounded-lg bg-primary text-caption font-bold text-primary-foreground shadow-subtle">
          M
        </span>
        <span className="hidden text-base tracking-tight sm:inline">{t('common:app.name')}</span>
      </div>

      <Separator orientation="vertical" className="mx-1 hidden h-6 sm:block" />

      <Button
        variant="outline"
        size="sm"
        onClick={onOpenSearch}
        className="hidden h-10 max-w-80 flex-1 justify-start gap-2 bg-muted/40 text-sm font-normal text-muted-foreground hover:bg-muted sm:flex"
        aria-label={t('common:commandPalette.open')}
        data-tour="global-search"
      >
        <Search className="size-4" />
        <span className="truncate">{t('common:commandPalette.placeholder')}</span>
        {/* `rem`, not `px` — a hard-coded 10px keycap is the one element in the topbar
            that would have ignored the root font-size lever and shrunk relative to it. */}
        <kbd className="ms-auto hidden rounded border bg-background px-1.5 py-0.5 font-mono text-[0.6875rem] md:inline">
          ⌘K
        </kbd>
      </Button>

      <div className="ms-auto flex items-center gap-1.5">
        <span data-tour="period-badge">
          <PeriodBadge />
        </span>

        <Button
          variant="ghost"
          size="icon-sm"
          className="sm:hidden"
          onClick={onOpenSearch}
          aria-label={t('common:commandPalette.open')}
        >
          <Search className="size-4" />
        </Button>

        <HelpMenu />
        <LanguageToggle />
        <ThemeToggle />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon-sm" aria-label={t('common:user.menu')}>
              <UserIcon className="size-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel className="font-normal">
              <span className="block text-caption text-muted-foreground">{t('common:user.signedInAs')}</span>
              <span className="block truncate font-medium text-foreground">{user?.name}</span>
              <span className="block truncate text-caption text-muted-foreground">{user?.email}</span>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/account')}>
              <UserIcon className="size-4" />
              {t('common:user.myAccount')}
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={logout}>
              <LogOut className="size-4 rtl-flip" />
              {t('common:actions.signOut')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
};

export default Topbar;
